import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { agents } from '../../../../../api/agents'

const ACTIVE_ID_KEY = ['portfolio-research/active-session-id']
const POLL_MS = 1500
const SETTLED_THREAD_STATUSES = new Set(['done', 'error', 'cancelled', 'ended'])

// Tracks the investigation the user just started in this tab. The launcher
// (toolbar) and the tray (content) both call this and stay in sync via the
// shared React Query cache key — no provider, event bus, or localStorage.
// State is per-tab; lost on full reload (the session keeps running and is
// findable in history).
export function useActiveInvestigation () {
  const queryClient = useQueryClient()

  const { data: activeId } = useQuery({
    queryKey: ACTIVE_ID_KEY,
    queryFn: () => queryClient.getQueryData(ACTIVE_ID_KEY) ?? null,
    initialData: null,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })

  const trackSession = useCallback((id) => {
    queryClient.setQueryData(ACTIVE_ID_KEY, id ?? null)
  }, [queryClient])

  const { data: report } = agents.usePortfolioResearchReport(activeId, {
    enabled: activeId != null,
    refetchInterval: (latest) => {
      if (!latest) return POLL_MS
      return SETTLED_THREAD_STATUSES.has(latest.threadStatus) ? false : POLL_MS
    }
  })

  useEffect(() => {
    if (activeId != null && report && SETTLED_THREAD_STATUSES.has(report.threadStatus)) {
      trackSession(null)
    }
  }, [activeId, report, trackSession])

  const isBusy = activeId != null && (!report || !SETTLED_THREAD_STATUSES.has(report.threadStatus))

  return { activeId, isBusy, trackSession }
}
